/**
 * How a subagent's liveness reads in one line: what it is doing now and for
 * how long. Every surface that shows a delegated worker (the chat's spawn
 * row, the composer's background-work banner, the Agents panel) derives its
 * copy here, so a worker that is busy never looks stopped in one place and
 * running in another.
 *
 * Inputs are the orchestration-v2 subagent fields (`status`, `progress`,
 * `startedAt`, `completedAt`, `updatedAt`), and `formatSubagentStatus`
 * follows v2's `formatProviderSubagentStatus` (pingdotgg/t3code #2829,
 * client-runtime `threadExecution.ts`): "Working 12s" while live, "Completed
 * in 34s" once done. Today the fields come from the v1 activity fold in
 * `subagentRuntime.ts`; when the v2 projection replaces that fold, this
 * module keeps working unchanged.
 */
import {
  isActiveSubagentStatus,
  type RuntimeSubagent,
  type RuntimeSubagentStatus,
} from "./subagentRuntime.ts";

type SubagentTiming = Pick<RuntimeSubagent, "status" | "startedAt" | "completedAt">;

type SubagentActivitySource = Pick<
  RuntimeSubagent,
  "status" | "progress" | "lastToolName" | "result" | "error"
>;

/**
 * In-flight states all read as Working: a waiting or queued worker is still
 * the fleet doing its job, not a user problem. Only settled states differ.
 */
export const SUBAGENT_STATUS_LABELS: Record<RuntimeSubagentStatus, string> = {
  pending: "Working",
  running: "Working",
  waiting: "Working",
  idle: "Idle · resumable",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Stopped",
  interrupted: "Stopped",
};

/** "12s", "4m 05s", "1h 02m". Whole seconds, so a ticking label never flickers. */
export function formatSubagentElapsed(totalMs: number): string {
  const seconds = Math.max(0, Math.floor(totalMs / 1000));
  const minutes = Math.floor(seconds / 60);
  if (minutes === 0) {
    return `${seconds}s`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours === 0) {
    return `${minutes}m ${String(seconds % 60).padStart(2, "0")}s`;
  }
  return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/**
 * Time this activation has run: to now while live, to `completedAt` once
 * settled. Null when the start (or a settled run's end) is unknown, so a
 * caller never renders "0s" for a run it cannot time.
 */
export function subagentElapsedMs(agent: SubagentTiming, nowMs: number): number | null {
  if (agent.startedAt === null) return null;
  const start = Date.parse(agent.startedAt);
  const live = isActiveSubagentStatus(agent.status);
  const end = live
    ? nowMs
    : agent.completedAt === null
      ? Number.NaN
      : Date.parse(agent.completedAt);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.max(0, end - start);
}

export function formatSubagentElapsedLabel(agent: SubagentTiming, nowMs: number): string {
  const elapsed = subagentElapsedMs(agent, nowMs);
  return elapsed === null ? "" : formatSubagentElapsed(elapsed);
}

/**
 * One status line: "Working 4m 12s", "Completed in 14m 13s", or the bare
 * label when no duration is known. Failed and stopped runs carry no duration:
 * how long a run took before it broke is not what the reader needs first.
 */
export function formatSubagentStatus(agent: SubagentTiming, nowMs: number): string {
  const label = SUBAGENT_STATUS_LABELS[agent.status];
  const live = isActiveSubagentStatus(agent.status);
  if (!live && agent.status !== "completed") return label;
  const elapsed = subagentElapsedMs(agent, nowMs);
  if (elapsed === null) return label;
  const duration = formatSubagentElapsed(Math.max(1000, elapsed));
  return live ? `${label} ${duration}` : `${label} in ${duration}`;
}

/**
 * What the agent is doing (live) or how it ended (settled), as raw progress
 * text. Live rows lead with the latest step; settled rows lead with the
 * outcome. The text is returned whole: a progress line can carry multi-line
 * JSON that a presenter parses before it picks the line to show.
 */
export function subagentActivityText(agent: SubagentActivitySource): string | null {
  const toolLine = agent.lastToolName ? `▸ ${agent.lastToolName}` : null;
  return isActiveSubagentStatus(agent.status)
    ? (agent.progress ?? toolLine ?? agent.result ?? agent.error)
    : (agent.error ?? agent.result ?? agent.progress ?? toolLine);
}

/** First non-empty line, for surfaces that show a single line of activity. */
export function firstActivityLine(text: string | null): string | null {
  if (text === null) return null;
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (trimmed.length > 0) return trimmed;
  }
  return null;
}

/**
 * The live agent a one-line summary should speak for: the one that reported
 * most recently, so the line follows whichever worker is actually moving.
 */
export function leadLiveSubagent<T extends Pick<RuntimeSubagent, "status" | "updatedAt">>(
  agents: ReadonlyArray<T>,
): T | null {
  let lead: T | null = null;
  for (const agent of agents) {
    if (!isActiveSubagentStatus(agent.status)) continue;
    if (lead === null || agent.updatedAt > lead.updatedAt) lead = agent;
  }
  return lead;
}
